import { format } from 'date-fns';

import type {
  PublicTermsCoverage,
  PublicTermsEntityFamily,
  TermsRole,
} from '@/services/public-terms';
import type { TermsScope } from '@/services/terms';

// Mirrors TERMS_PATH_SEGMENTS in the backend terms service; the backend emits
// these paths and this table reads them back.
const TERMS_ROLE_SEGMENTS: Record<string, TermsScope> = {
  studio: 'STUDIO',
  classes: 'CLASS',
  'class-packs': 'CLASS_PACK',
  subscriptions: 'SUBSCRIPTION',
  'top-ups': 'TOPUP',
  'gift-cards': 'GIFT_CARD',
};

const ENTITY_FAMILIES: TermsScope[] = [
  'CLASS',
  'CLASS_PACK',
  'SUBSCRIPTION',
  'GIFT_CARD',
];

const FAMILY_ALL_LABELS: Record<TermsScope, string> = {
  STUDIO: 'every booking and purchase',
  CLASS: 'all classes',
  CLASS_PACK: 'all class packs',
  SUBSCRIPTION: 'all memberships',
  TOPUP: 'top-ups',
  GIFT_CARD: 'all gift cards',
};

const FAMILY_NOUNS: Record<PublicTermsEntityFamily, [string, string]> = {
  CLASS: ['class', 'classes'],
  CLASS_PACK: ['class pack', 'class packs'],
  SUBSCRIPTION: ['membership', 'memberships'],
  GIFT_CARD: ['gift card', 'gift cards'],
};

const MAX_NAMED_ENTITIES = 3;

// The backend's slugSchema bounds. A slug it would refuse outright is a bad
// address, not an empty place, so it 404s like an unknown segment.
const SLUG_MIN_LENGTH = 2;
const SLUG_MAX_LENGTH = 255;

export const parseTermsRole = (
  segments: string | string[] | undefined
): TermsRole | null => {
  const parts = Array.isArray(segments) ? segments : segments ? [segments] : [];
  if (parts.length < 1 || parts.length > 2) return null;
  const [familySegment, slug] = parts;
  // Object.hasOwn, not a plain lookup: a segment like "constructor" must not
  // match an inherited Object.prototype key.
  if (!Object.hasOwn(TERMS_ROLE_SEGMENTS, familySegment)) return null;
  const type = TERMS_ROLE_SEGMENTS[familySegment];
  if (!slug) return { type };
  if (slug.length < SLUG_MIN_LENGTH || slug.length > SLUG_MAX_LENGTH) {
    return null;
  }
  return ENTITY_FAMILIES.includes(type) ? { type, slug } : null;
};

export const joinWithAnd = (parts: string[]): string =>
  parts.length <= 1
    ? parts.join('')
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

const countByFamily = (
  entities: Extract<PublicTermsCoverage, { scope: 'ENTITY' }>[]
): string[] => {
  const counts = new Map<PublicTermsEntityFamily, number>();
  for (const entity of entities) {
    counts.set(entity.family, (counts.get(entity.family) ?? 0) + 1);
  }
  return [...counts.entries()].map(([family, count]) => {
    const [singular, plural] = FAMILY_NOUNS[family];
    return `${count} ${count === 1 ? singular : plural}`;
  });
};

export const getCoverageSentence = (
  coverage: PublicTermsCoverage[]
): string => {
  if (coverage.some((entry) => entry.scope === 'STUDIO')) {
    return `Applies to ${FAMILY_ALL_LABELS.STUDIO}`;
  }
  const parts = coverage.flatMap((entry) =>
    entry.scope === 'FAMILY' ? [FAMILY_ALL_LABELS[entry.family]] : []
  );
  const entities = coverage.filter(
    (entry): entry is Extract<PublicTermsCoverage, { scope: 'ENTITY' }> =>
      entry.scope === 'ENTITY'
  );
  parts.push(
    ...(entities.length > MAX_NAMED_ENTITIES
      ? countByFamily(entities)
      : entities.map((entity) => entity.entity_name))
  );
  return parts.length ? `Applies to ${joinWithAnd(parts)}` : '';
};

export const formatTermsDate = (date: string): string =>
  format(new Date(date), 'MMM d, yyyy');
